import { NavLink, useLocation } from 'react-router-dom';

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { usePendingApprovals } from '@/features/approvals';
import { usePermissions } from '@/features/auth';
import { useLowBalanceProviderCount } from '@/features/providers';
import { Logo, LogoLockup } from '@/shared/components/brand/logo';
import { cn } from '@/shared/lib/utils';

import { canSee, NAVIGATION, type NavItem } from '../../config/navigation';

/**
 * Primary navigation.
 *
 * Reads entirely from `app/config/navigation.ts` — no route list is duplicated
 * here (§20). Items the current role cannot access are filtered out, and a
 * section whose items are all filtered out disappears with them rather than
 * leaving an empty heading.
 */
export function AdminSidebar() {
  const { can } = usePermissions();
  const { state } = useSidebar();
  const location = useLocation();

  const collapsed = state === 'collapsed';
  const pendingApprovals = usePendingApprovals(can('approvals.read'));
  const lowBalanceProviders = useLowBalanceProviderCount(can('providers.read'));
  const badges: Record<NonNullable<NavItem['badge']>, NavBadge> = {
    pendingApprovals: {
      count: pendingApprovals.data?.length ?? 0,
      label: (count) => `${count} waiting`,
      tone: 'warning',
    },
    lowBalanceProviders: {
      count: lowBalanceProviders.data ?? 0,
      label: (count) =>
        count === 1 ? '1 provider low on balance' : `${count} providers low on balance`,
      tone: 'danger',
    },
  };

  const sections = NAVIGATION.map((section) => ({
    ...section,
    items: section.items.filter((item) => canSee(item, can)),
  })).filter((section) => section.items.length > 0);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="h-14 justify-center px-3">
        {collapsed ? (
          <Logo height={24} className="mx-auto" />
        ) : (
          <LogoLockup height={26} className="px-1" />
        )}
      </SidebarHeader>

      <SidebarContent className="gap-0">
        {sections.map((section) => (
          <SidebarGroup key={section.title}>
            <SidebarGroupLabel className="text-[10px] font-semibold tracking-[0.1em] uppercase">
              {section.title}
            </SidebarGroupLabel>
            <SidebarMenu>
              {section.items.map((item) => (
                <SidebarNavItem
                  key={item.path}
                  item={item}
                  collapsed={collapsed}
                  currentPath={location.pathname}
                  badge={item.badge ? badges[item.badge] : undefined}
                />
              ))}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarRail />
    </Sidebar>
  );
}

/** A live count next to a nav entry, telling staff something needs action. */
interface NavBadge {
  count: number;
  /** Read by screen readers and shown in the collapsed tooltip. */
  label: (count: number) => string;
  tone: 'warning' | 'danger';
}

const BADGE_TONE: Record<NavBadge['tone'], { pill: string; dot: string }> = {
  warning: {
    pill: 'bg-status-warning-bg text-status-warning border-status-warning-border',
    dot: 'bg-status-warning',
  },
  danger: {
    pill: 'bg-status-danger-bg text-status-danger border-status-danger-border',
    dot: 'bg-status-danger',
  },
};

function SidebarNavItem({
  item,
  collapsed,
  currentPath,
  badge,
}: {
  item: NavItem;
  collapsed: boolean;
  currentPath: string;
  /** Nothing is shown when absent or at 0. */
  badge: NavBadge | undefined;
}) {
  const Icon = item.icon;
  const count = badge?.count ?? 0;

  // Modules that exist in the product but are not built yet render as disabled
  // rather than being hidden, so the intended shape of the admin stays legible
  // and nobody navigates into a 404.
  if (item.status === 'planned') {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          disabled
          tooltip={collapsed ? `${item.title} — coming soon` : undefined}
          className="cursor-not-allowed opacity-45"
        >
          <Icon aria-hidden />
          <span className="flex-1">{item.title}</span>
          {!collapsed && (
            <span className="text-muted-foreground border-border rounded border px-1 py-px text-[9px] font-medium tracking-wide uppercase">
              Soon
            </span>
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  const isActive = currentPath === item.path || currentPath.startsWith(`${item.path}/`);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isActive}
        tooltip={
          collapsed
            ? badge && count > 0
              ? `${item.title} (${badge.label(count)})`
              : item.title
            : undefined
        }
        className={cn(
          // Active state: a purple-tinted surface and a brand icon, not a
          // saturated block. Restraint is what makes it read as "you are here"
          // rather than as decoration (§34).
          'data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground data-[active=true]:font-semibold',
          '[&[data-active=true]_svg]:text-sidebar-primary',
        )}
      >
        <NavLink to={item.path}>
          <Icon aria-hidden />
          <span>{item.title}</span>
          {badge && count > 0 && collapsed && (
            // The count badge is hidden when collapsed; a dot keeps the signal.
            <span
              aria-hidden
              className={cn('absolute end-1 top-1 size-2 rounded-full', BADGE_TONE[badge.tone].dot)}
            />
          )}
          {isActive && (
            // A slim gradient marker on the inline edge. Logical property, so
            // it moves to the right-hand side automatically under dir="rtl".
            <span
              aria-hidden
              className="bg-brand-gradient absolute inset-y-1 start-0 w-[3px] rounded-e-full"
            />
          )}
        </NavLink>
      </SidebarMenuButton>
      {badge && count > 0 && (
        <SidebarMenuBadge
          className={cn('border', BADGE_TONE[badge.tone].pill)}
          aria-label={badge.label(count)}
        >
          {count > 99 ? '99+' : count}
        </SidebarMenuBadge>
      )}
    </SidebarMenuItem>
  );
}
